import React, { ComponentClass, ReactNode, useState } from 'react';
import findResponsiveItem from 'presentation/utils/findResponsiveItem';
import visibilitySet from 'presentation/hocs/visibilitySet';
import { Fit, RibbonProps, Type } from 'domain/constants/ribbons';
import { scroller, Element } from 'react-scroll';
import { ulid } from 'ulid';
import errorBoundary from 'presentation/hocs/errorBoundary';
import { useBreakpoints } from '@ux/responsive';
import { Layout } from 'domain/constants/layout';
import { Justify } from '@uds/grid';
import { Background } from 'components/common/Background';

import CornerEmbellishments, { CornerEmbellishmentsType } from './CornerEmbellishments';
import BillboardGeneric from './layouts/Generic';
import BillboardDuo from './layouts/Duo';
import BillboardTiles from './layouts/WithTiles';
import { OuterProps as BodyProps } from './Body';
import { TileProps } from './Tile';
import Ribbon from '../Ribbon';

export interface OuterProps extends BodyProps, RibbonProps {
  cornerEmbellishments: CornerEmbellishmentsType;
  tiles?: Array<TileProps>;
  additionalText?: ReactNode;
  footerText?: ReactNode;
  duo?: boolean;
  duoLayout?: boolean;
  name: string;
  loading: boolean;
  id: string;
  justify: Justify;
  background: [Background, Background, Background];
  hideGradient?: boolean;
}
interface LayoutProps extends Omit<OuterProps, 'cornerEmbellishments'> {
  hasTiles: boolean;
  showScroll: boolean;
  onScrollClick: () => void;
  setBottomScrollName: (v: string) => void;
}

const shouldShowScroll = (props: {
  layout: Layout;
  isMinSm: boolean;
  isMinMd: boolean;
  isMinLg: boolean;
  isMinXl: boolean;
}) => {
  const { isMinSm, isMinMd, isMinLg, isMinXl, layout } = props;
  if (props.isMinSm) {
    const fitSet = layout.fit;
    const fit = findResponsiveItem(
      {
        isMinSm,
        isMinMd,
        isMinLg,
        isMinXl,
      },
      fitSet,
    );
    return fit === Fit.FILL || fit === Fit.FULLSCREEN;
  }
  return false;
};

const BillboardLayout = ({ hasTiles, tiles = [], duoLayout, ...props }: LayoutProps) => {
  if (hasTiles) {
    return <BillboardTiles {...props} tiles={tiles} />;
  }
  if (duoLayout) {
    return <BillboardDuo {...props} duoLayout={duoLayout} />;
  }
  return <BillboardGeneric {...props} />;
};

export const PureBillboard = ({
  id,
  layout,
  loading,
  colorway,
  name,
  duoLayout,
  cornerEmbellishments,
  ctas = [],
  additionalText = null,
  footerText = null,
  duo = false,
  tiles,
  ...props
}: OuterProps) => {
  const breakpoints = useBreakpoints();

  const showScroll = shouldShowScroll({
    layout,
    ...breakpoints,
  });

  const hasTiles = tiles?.length === 2 && duo === false;

  const [bottomScrollName, setBottomScrollName] = useState(() => {
    return ulid();
  });
  const scrollHandler =
    ({ bottomScrollName }: { bottomScrollName: string }) =>
    () => {
      scroller.scrollTo(bottomScrollName, {
        duration: 500,
        delay: 100,
        smooth: true,
      });
    };
  const onScrollClick = scrollHandler({ bottomScrollName });

  return (
    <>
      <Ribbon
        id={id}
        colorway={colorway}
        layout={layout}
        loading={loading}
        name={name}
        type={Type.BILLBOARD}
        flush={hasTiles}
        duoLayout={duoLayout}
      >
        <BillboardLayout
          colorway={colorway}
          layout={layout}
          hasTiles={hasTiles}
          duoLayout={duoLayout}
          ctas={ctas}
          additionalText={additionalText}
          footerText={footerText}
          duo={duo}
          showScroll={showScroll}
          onScrollClick={onScrollClick}
          setBottomScrollName={setBottomScrollName}
          name={name}
          loading={loading}
          id={id}
          {...props}
          ribbonId={id}
          tiles={tiles}
        />
        <CornerEmbellishments cornerEmbellishments={cornerEmbellishments} />
      </Ribbon>
      <Element name={bottomScrollName} />
    </>
  );
};

PureBillboard.displayName = 'Billboard';

export default errorBoundary(visibilitySet(PureBillboard)) as ComponentClass<OuterProps>;
